import { useMemo } from "react";
import {
  Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { CalendarCheck, Clock, Pulse, Target } from "@phosphor-icons/react";
import { useAuth } from "../lib/api";

const number = (value, digits = 1) => (
  value === null || value === undefined ? "—" : Number(value).toFixed(digits)
);

const dateValue = (value) => {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

function TimelineTooltip({ active, payload }) {
  const point = payload?.find((item) => item?.payload)?.payload;
  if (!active || !point) return null;
  return (
    <div className="border border-border bg-background p-3 shadow-lg text-xs min-w-60">
      <div className="font-mono-data font-bold">{point.title}</div>
      <div className="text-muted-foreground mt-1">{point.fullDate}</div>
      {point.kind === "exam" ? (
        <>
          <div className="font-display text-2xl font-black text-primary mt-2">{number(point.score)}%</div>
          <div className="text-muted-foreground mt-1">{point.hits}/{point.totalValid} acertos</div>
        </>
      ) : (
        <div className="space-y-1 mt-2">
          <div><b>{number(point.questions, 0)}</b> questões realizadas</div>
          <div><b>{number(point.hours, 1)} h</b> de uso</div>
          <div><b>{number(point.medcofAccuracy)}%</b> de acerto MEDCOF</div>
        </div>
      )}
    </div>
  );
}

function StaticLegend({ hasMedcof }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-mono-data" aria-label="Legenda do gráfico">
      <span className="inline-flex items-center gap-2"><span className="h-[3px] w-6 bg-primary" />Nota nas provas</span>
      {hasMedcof && <span className="inline-flex items-center gap-2"><span className="h-3 w-3 bg-[hsl(var(--warning))]" />Questões MEDCOF</span>}
      {hasMedcof && <span className="inline-flex items-center gap-2"><span className="h-[3px] w-6 bg-[hsl(var(--success))]" />Horas MEDCOF</span>}
    </div>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

const uniformTimeTicks = (points) => {
  const values = points.map((item) => item.timestamp).filter(Number.isFinite);
  if (!values.length) return { domain: ["auto", "auto"], ticks: [] };
  let minimum = Math.min(...values);
  let maximum = Math.max(...values);
  if (minimum === maximum) {
    minimum -= DAY_MS;
    maximum += DAY_MS;
  }
  const count = 6;
  const step = (maximum - minimum) / (count - 1);
  return {
    domain: [minimum, maximum],
    ticks: Array.from({ length: count }, (_, index) => Math.round(minimum + (step * index))),
  };
};

const timeTick = (value) => new Date(Number(value)).toLocaleDateString("pt-BR", {
  day: "2-digit", month: "2-digit", year: "2-digit",
});

export function LearningTimelineChart({ progression = [], history = [], height = 360, compact = false }) {
  const hasMedcof = history.length > 0;
  const timeline = useMemo(() => {
    const exams = progression.map((item, index) => {
      const date = dateValue(item.date);
      return {
        key: `exam-${item.exam_id || index}`,
        kind: "exam",
        timestamp: date?.getTime(),
        fullDate: date?.toLocaleDateString("pt-BR") || "Data não informada",
        title: item.exam_name || "Prova",
        score: Number(item.score || 0),
        hits: item.hits,
        totalValid: item.total_valid,
        questions: null,
        hours: null,
      };
    });
    const medcof = history.map((item, index) => {
      const date = dateValue(item.period_end || item.period_start);
      return {
        key: `medcof-${item.period_start || index}-${item.period_end || index}`,
        kind: "medcof",
        timestamp: date?.getTime(),
        fullDate: item.period_label || "Período MEDCOF",
        title: `MEDCOF · ${item.period_label || "período"}`,
        score: null,
        hits: null,
        totalValid: null,
        questions: Number(item.metrics?.questions || 0),
        hours: Number(item.metrics?.time_minutes || 0) / 60,
        medcofAccuracy: item.metrics?.accuracy_percent,
      };
    });
    return [...exams, ...medcof]
      .filter((item) => Number.isFinite(item.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp || a.kind.localeCompare(b.kind));
  }, [progression, history]);
  const axis = useMemo(() => uniformTimeTicks(timeline), [timeline]);

  if (!timeline.length) {
    return <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">Nenhuma prova realizada com correção válida.</div>;
  }

  return (
    <div className="border border-border p-4" data-testid="uniform-learning-timeline">
      <StaticLegend hasMedcof={hasMedcof} />
      <div className="mt-3" style={{ width: "100%", minWidth: compact ? "620px" : "680px", height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%" debounce={100}>
          <ComposedChart data={timeline} margin={{ top: 12, right: hasMedcof ? 32 : 18, left: -8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical />
            <XAxis dataKey="timestamp" type="number" scale="time" domain={axis.domain} ticks={axis.ticks} tickFormatter={timeTick} tick={{ fontSize: 10 }} />
            <YAxis yAxisId="score" domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tick={{ fontSize: 10 }} unit="%" />
            {hasMedcof && <YAxis yAxisId="questions" orientation="right" allowDecimals={false} tick={{ fontSize: 10 }} />}
            {hasMedcof && <YAxis yAxisId="hours" hide domain={[0, "auto"]} />}
            <Tooltip content={<TimelineTooltip />} />
            <ReferenceLine yAxisId="score" y={60} stroke="hsl(var(--success))" strokeDasharray="5 5" />
            {hasMedcof && <Bar yAxisId="questions" dataKey="questions" name="Questões MEDCOF" fill="hsl(var(--warning))" barSize={compact ? 14 : 22} isAnimationActive={false} />}
            <Line yAxisId="score" type="linear" dataKey="score" name="Nota nas provas" connectNulls stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4, fill: "hsl(var(--background))", strokeWidth: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
            {hasMedcof && <Line yAxisId="hours" type="linear" dataKey="hours" name="Horas MEDCOF" connectNulls stroke="hsl(var(--success))" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 5 }} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function StudentLearningTimeline({ progression = [], engagement }) {
  const { user } = useAuth();
  const history = engagement?.history || [];
  const summary = engagement?.summary || {};
  const latest = summary.latest_metrics || {};
  const isStudent = Boolean(user?.student_id || user?.account_type === "student");
  const hasMedcof = history.length > 0;

  return (
    <section className="border border-border bg-card p-6 mb-8" data-testid="student-learning-timeline">
      <div className="label-eyebrow">progressão temporal individual</div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mt-1">
        <h2 className="font-display text-3xl font-black tracking-tight">{hasMedcof ? "Provas e dedicação ao longo do tempo" : "Acertos prova a prova"}</h2>
        <div className="text-xs text-muted-foreground font-mono-data">{progression.length} prova(s) realizada(s){hasMedcof ? ` · ${history.length} período(s) MEDCOF` : ""}</div>
      </div>

      <div className="overflow-x-auto mt-5">
        <LearningTimelineChart progression={progression} history={history} />
      </div>

      {hasMedcof && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="student-medcof-summary">
            <div className="border border-border p-4"><Pulse size={19} className="text-primary" /><div className="label-eyebrow mt-3">engajamento atual</div><div className="font-display text-2xl font-black mt-1">{latest.engagement || "—"}</div></div>
            <div className="border border-border p-4"><CalendarCheck size={19} className="text-primary" /><div className="label-eyebrow mt-3">questões por dia</div><div className="font-display text-2xl font-black mt-1">{number(latest.questions_per_day)}</div></div>
            <div className="border border-border p-4"><Clock size={19} className="text-primary" /><div className="label-eyebrow mt-3">tempo no período</div><div className="font-display text-2xl font-black mt-1">{number(Number(latest.time_minutes || 0) / 60, 1)} h</div></div>
            <div className="border border-border p-4"><Target size={19} className="text-primary" /><div className="label-eyebrow mt-3">acerto MEDCOF</div><div className="font-display text-2xl font-black mt-1">{number(latest.accuracy_percent)}%</div></div>
          </div>
          <div className="overflow-x-auto mt-5 border border-border" data-testid="student-medcof-history">
            <div className="min-w-[880px] grid grid-cols-[190px_130px_110px_110px_110px_130px_1fr] gap-3 px-4 py-3 bg-muted/30 label-eyebrow">
              <div>Período</div><div>Engajamento</div><div>Questões</div><div>Q./dia</div><div>Acerto</div><div>Dias de estudo</div><div>Tempo de uso</div>
            </div>
            {history.map((item) => (
              <div key={`${item.period_start}-${item.period_end}`} className="min-w-[880px] grid grid-cols-[190px_130px_110px_110px_110px_130px_1fr] gap-3 px-4 py-3 border-t border-border text-sm items-center">
                <div className="font-mono-data text-xs">{item.period_label}</div>
                <div className="font-semibold">{item.metrics?.engagement || "—"}</div>
                <div className="font-mono-data">{number(item.metrics?.questions, 0)}</div>
                <div className="font-mono-data">{number(item.metrics?.questions_per_day)}</div>
                <div className="font-mono-data">{number(item.metrics?.accuracy_percent)}%</div>
                <div className="font-mono-data">{number(item.metrics?.study_days, 0)}</div>
                <div>{item.metrics?.time_label || `${number(item.metrics?.time_minutes, 0)} min`}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {!hasMedcof && !isStudent && (
        <div className="border border-dashed border-border p-4 text-sm text-muted-foreground mt-5" data-testid="student-medcof-empty-staff">Este aluno ainda não possui histórico MEDCOF importado ou vinculado.</div>
      )}
    </section>
  );
}
